import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getAusencias, crearAusencia, actualizarAusencia, eliminarAusencia,
  getEventos, crearEvento, actualizarEvento, eliminarEvento,
  getVacacionesResumen, actualizarVacacionesConfig,
  getComunidades,
} from '../services/api';
import { useToast } from '../components/Toast';

const TIPOS_AUSENCIA = {
  vacaciones:       { label: 'Vacaciones',      color: '#3b82f6' },
  baja_medica:      { label: 'Baja médica',     color: '#ef4444' },
  permiso_personal: { label: 'Permiso personal', color: '#f59e0b' },
  dia_libre:        { label: 'Día libre',        color: '#10b981' },
};

const TIPOS_EVENTO = {
  junta_propietarios: { label: 'Junta de propietarios', color: '#8b5cf6' },
  reunion:            { label: 'Reunión',               color: '#06b6d4' },
  recordatorio:       { label: 'Recordatorio',          color: '#6b7280' },
};

const ESTADOS = {
  pendiente: { label: 'Pendiente', color: '#f59e0b' },
  aprobada:  { label: 'Aprobada',  color: '#10b981' },
  rechazada: { label: 'Rechazada', color: '#ef4444' },
};

function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDayOfMonth(y, m) { const d = new Date(y, m, 1).getDay(); return d === 0 ? 6 : d - 1; }
function dateInRange(ds, ini, fin) { return ds >= ini && ds <= fin; }
function pad(n) { return String(n).padStart(2, '0'); }

const EMPTY_AUSENCIA = { usuario_id: '', comunidad_id: '', tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' };
const EMPTY_EVENTO   = { titulo: '', tipo: 'junta_propietarios', fecha_inicio: '', fecha_fin: '', hora_inicio: '', hora_fin: '', comunidad_id: '', ubicacion: '', descripcion: '', todo_el_dia: true };

export default function Ausencias({ user }) {
  const { showToast } = useToast();
  const today = new Date();
  const [mes, setMes] = useState(today.getMonth());
  const [anio, setAnio] = useState(today.getFullYear());
  const [ausencias, setAusencias] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [vacaciones, setVacaciones] = useState([]);
  const [comunidades, setComunidades] = useState([]);
  const [selectedComunidad, setSelectedComunidad] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ausencias');

  // Modal ausencia
  const [showAusModal, setShowAusModal] = useState(false);
  const [editingAus, setEditingAus] = useState(null);
  const [ausForm, setAusForm] = useState(EMPTY_AUSENCIA);
  const ausModalCardRef = useRef(null);

  // Modal evento
  const [showEvModal, setShowEvModal] = useState(false);
  const [editingEv, setEditingEv] = useState(null);
  const [evForm, setEvForm] = useState(EMPTY_EVENTO);
  const evModalCardRef = useRef(null);

  // Vacaciones: edición inline de días totales
  const [editingVac, setEditingVac] = useState(null); // usuario_id
  const [vacDias, setVacDias] = useState(22);

  const esPrincipal = user?.rol === 'administrador';
  const esGestor = ['administrador', 'empleado'].includes(user?.rol);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mes: mes + 1, anio };
      if (selectedComunidad) params.comunidad_id = selectedComunidad;
      const evParams = { mes: mes + 1, anio };
      if (selectedComunidad) evParams.comunidad_id = selectedComunidad;
      const [ausRes, evRes, comRes] = await Promise.all([
        getAusencias(params),
        getEventos(evParams),
        getComunidades(),
      ]);
      setAusencias(ausRes.data || []);
      setEventos(evRes.data || []);
      setComunidades(comRes.data || []);
    } catch { showToast('Error al cargar datos', 'error'); }
    finally { setLoading(false); }
  }, [mes, anio, selectedComunidad]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarVacaciones = useCallback(async () => {
    try {
      const res = await getVacacionesResumen(anio);
      setVacaciones(res.data || []);
    } catch { /* silencioso */ }
  }, [anio]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);
  useEffect(() => { if (activeTab === 'vacaciones') cargarVacaciones(); }, [activeTab, cargarVacaciones]);
  useEffect(() => { if (showAusModal && ausModalCardRef.current) ausModalCardRef.current.scrollTop = 0; }, [showAusModal]);
  useEffect(() => { if (showEvModal && evModalCardRef.current) evModalCardRef.current.scrollTop = 0; }, [showEvModal]);

  const handleMes = (dir) => {
    let m = mes + dir, a = anio;
    if (m < 0) { m = 11; a--; }
    if (m > 11) { m = 0; a++; }
    setMes(m); setAnio(a);
  };

  // ===== AUSENCIAS =====
  const openAusModal = (aus = null) => {
    if (aus) {
      setEditingAus(aus);
      setAusForm({ usuario_id: aus.usuario_id, comunidad_id: aus.comunidad_id, tipo: aus.tipo,
        fecha_inicio: aus.fecha_inicio?.slice(0, 10) || '', fecha_fin: aus.fecha_fin?.slice(0, 10) || '',
        motivo: aus.motivo || '' });
    } else {
      setEditingAus(null);
      const defCom = selectedComunidad || (comunidades.length === 1 ? comunidades[0].id : '');
      setAusForm({ ...EMPTY_AUSENCIA, usuario_id: user?.id || '', comunidad_id: defCom });
    }
    setShowAusModal(true);
  };

  const handleAusSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAus) { await actualizarAusencia(editingAus.id, ausForm); showToast('Ausencia actualizada', 'success'); }
      else { await crearAusencia(ausForm); showToast('Ausencia solicitada', 'success'); }
      setShowAusModal(false); cargarDatos();
    } catch (err) { showToast(err.response?.data?.error || 'Error', 'error'); }
  };

  const handleEstado = async (id, estado) => {
    try {
      await actualizarAusencia(id, { estado });
      showToast(estado === 'aprobada' ? 'Ausencia aprobada' : 'Rechazada', 'success');
      cargarDatos();
    } catch { showToast('Error', 'error'); }
  };

  const handleEliminarAus = async (id) => {
    if (!window.confirm('¿Eliminar esta ausencia?')) return;
    try { await eliminarAusencia(id); showToast('Eliminada', 'success'); cargarDatos(); }
    catch { showToast('Error', 'error'); }
  };

  // ===== EVENTOS =====
  const openEvModal = (ev = null) => {
    if (ev) {
      setEditingEv(ev);
      setEvForm({ titulo: ev.titulo, tipo: ev.tipo, fecha_inicio: ev.fecha_inicio?.slice(0, 10) || '',
        fecha_fin: ev.fecha_fin?.slice(0, 10) || '', hora_inicio: ev.hora_inicio || '',
        hora_fin: ev.hora_fin || '', comunidad_id: ev.comunidad_id || '',
        ubicacion: ev.ubicacion || '', descripcion: ev.descripcion || '', todo_el_dia: ev.todo_el_dia !== false });
    } else {
      setEditingEv(null);
      const defCom = selectedComunidad || (comunidades.length === 1 ? comunidades[0].id : '');
      setEvForm({ ...EMPTY_EVENTO, comunidad_id: defCom });
    }
    setShowEvModal(true);
  };

  const handleEvSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEv) { await actualizarEvento(editingEv.id, evForm); showToast('Evento actualizado', 'success'); }
      else { await crearEvento(evForm); showToast('Evento creado', 'success'); }
      setShowEvModal(false); cargarDatos();
    } catch (err) { showToast(err.response?.data?.error || 'Error', 'error'); }
  };

  const handleEliminarEv = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try { await eliminarEvento(id); showToast('Eliminado', 'success'); cargarDatos(); }
    catch { showToast('Error', 'error'); }
  };

  // ===== VACACIONES CONFIG =====
  const handleSaveVac = async (usuarioId) => {
    try {
      await actualizarVacacionesConfig(usuarioId, { dias_totales: vacDias, anio });
      showToast('Días actualizados', 'success');
      setEditingVac(null);
      cargarVacaciones();
    } catch { showToast('Error', 'error'); }
  };

  // ===== CALENDARIO =====
  const daysInMonth = getDaysInMonth(anio, mes);
  const firstDay = getFirstDayOfMonth(anio, mes);
  const monthStr = `${anio}-${pad(mes + 1)}`;

  const ausenciasByDay = {};
  ausencias.forEach(a => {
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${monthStr}-${pad(d)}`;
      if (dateInRange(ds, a.fecha_inicio?.slice(0, 10), a.fecha_fin?.slice(0, 10))) {
        if (!ausenciasByDay[d]) ausenciasByDay[d] = [];
        ausenciasByDay[d].push({ ...a, _tipo: 'ausencia' });
      }
    }
  });

  const eventosByDay = {};
  eventos.forEach(ev => {
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${monthStr}-${pad(d)}`;
      const fi = ev.fecha_inicio?.slice(0, 10);
      const ff = (ev.fecha_fin || ev.fecha_inicio)?.slice(0, 10);
      if (dateInRange(ds, fi, ff)) {
        if (!eventosByDay[d]) eventosByDay[d] = [];
        eventosByDay[d].push({ ...ev, _tipo: 'evento' });
      }
    }
  });

  const nombreMes = new Date(anio, mes, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  return (
    <div className="container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      {/* Cabecera */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700 }}>Calendario</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          {esGestor && <button className="btn btn-outline" onClick={() => openEvModal()}>+ Nuevo evento</button>}
          <button className="btn btn-primary" onClick={() => openAusModal()}>+ Nueva ausencia</button>
        </div>
      </div>

      {/* Filtro comunidad */}
      {esGestor && comunidades.length > 1 && (
        <div className="card" style={{ marginBottom: 16, padding: '10px 14px' }}>
          <select className="form-control" value={selectedComunidad} onChange={e => setSelectedComunidad(e.target.value)} style={{ maxWidth: 300 }}>
            <option value="">Todas las comunidades</option>
            {comunidades.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
      )}

      {/* Leyenda */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', fontSize: 12 }}>
        {Object.entries(TIPOS_AUSENCIA).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: v.color }} />
            {v.label}
          </div>
        ))}
        <div style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />
        {Object.entries(TIPOS_EVENTO).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: v.color }} />
            {v.label}
          </div>
        ))}
      </div>

      {/* Navegación mes */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <button className="btn btn-outline" style={{ padding: '5px 13px' }} onClick={() => handleMes(-1)}>&#8249;</button>
        <span style={{ fontWeight: 600, fontSize: 16, textTransform: 'capitalize', minWidth: 160, textAlign: 'center' }}>{nombreMes}</span>
        <button className="btn btn-outline" style={{ padding: '5px 13px' }} onClick={() => handleMes(1)}>&#8250;</button>
        <button className="btn btn-outline" style={{ padding: '5px 13px', fontSize: 12, marginLeft: 8 }}
          onClick={() => { setMes(today.getMonth()); setAnio(today.getFullYear()); }}>Hoy</button>
      </div>

      {/* Calendario */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 3 }}>
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', padding: '3px 0' }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const dayAus = ausenciasByDay[day] || [];
              const dayEv  = eventosByDay[day] || [];
              const isToday = anio === today.getFullYear() && mes === today.getMonth() && day === today.getDate();
              const isWeekend = [5, 6].includes((new Date(anio, mes, day).getDay() + 6) % 7);
              return (
                <div key={day} style={{
                  minHeight: 68, border: `1px solid ${isToday ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 7, padding: '3px 5px',
                  background: isToday ? 'rgba(59,130,246,0.06)' : isWeekend ? 'rgba(0,0,0,0.02)' : 'transparent'
                }}>
                  <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--primary)' : isWeekend ? 'var(--text-secondary)' : 'var(--text-secondary)', marginBottom: 2 }}>{day}</div>
                  {dayEv.slice(0, 2).map(ev => (
                    <div key={`ev-${ev.id}`} title={`${TIPOS_EVENTO[ev.tipo]?.label}: ${ev.titulo}`}
                      onClick={() => openEvModal(ev)}
                      style={{ fontSize: 9, borderRadius: 10, padding: '1px 5px', marginBottom: 1,
                        background: TIPOS_EVENTO[ev.tipo]?.color, color: 'white', cursor: 'pointer',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ● {ev.titulo}
                    </div>
                  ))}
                  {dayAus.slice(0, 3 - Math.min(dayEv.length, 2)).map(a => (
                    <div key={`aus-${a.id}`} title={`${a.nombre} ${a.apellidos} — ${TIPOS_AUSENCIA[a.tipo]?.label} (${ESTADOS[a.estado]?.label})`}
                      style={{ fontSize: 9, borderRadius: 3, padding: '1px 4px', marginBottom: 1,
                        background: TIPOS_AUSENCIA[a.tipo]?.color + '25', color: TIPOS_AUSENCIA[a.tipo]?.color,
                        border: `1px solid ${TIPOS_AUSENCIA[a.tipo]?.color}50`,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        opacity: a.estado === 'rechazada' ? 0.4 : 1 }}>
                      {a.nombre?.split(' ')[0]}
                    </div>
                  ))}
                  {(dayAus.length + dayEv.length) > 4 && (
                    <div style={{ fontSize: 9, color: 'var(--text-secondary)' }}>+{dayAus.length + dayEv.length - 4}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginTop: 28, marginBottom: 16, borderBottom: '2px solid var(--border)' }}>
        {[
          { key: 'ausencias', label: `Ausencias (${ausencias.length})` },
          { key: 'eventos',   label: `Eventos (${eventos.length})` },
          esGestor && { key: 'vacaciones', label: 'Vacaciones' },
        ].filter(Boolean).map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: activeTab === tab.key ? 700 : 500, fontSize: 14,
              borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === tab.key ? 'var(--primary)' : 'var(--text-secondary)',
              marginBottom: -2 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Ausencias */}
      {activeTab === 'ausencias' && (
        ausencias.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
            No hay ausencias este mes
          </div>
        ) : (
          <div className="card">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Empleado</th><th>Tipo</th><th>Desde</th><th>Hasta</th>
                    <th>Días lab.</th><th>Estado</th><th>Motivo</th>
                    <th style={{ textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ausencias.map(a => (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 500 }}>{a.nombre} {a.apellidos}</td>
                      <td><span style={{ background: TIPOS_AUSENCIA[a.tipo]?.color + '22', color: TIPOS_AUSENCIA[a.tipo]?.color, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{TIPOS_AUSENCIA[a.tipo]?.label}</span></td>
                      <td>{a.fecha_inicio?.slice(0, 10)}</td>
                      <td>{a.fecha_fin?.slice(0, 10)}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{a.dias_habiles ?? '—'}</td>
                      <td><span style={{ background: ESTADOS[a.estado]?.color + '22', color: ESTADOS[a.estado]?.color, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{ESTADOS[a.estado]?.label}</span></td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{a.motivo || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {esPrincipal && a.estado === 'pendiente' && (<>
                            <button className="btn btn-sm" style={{ background: '#10b981', color: 'white', border: 'none' }} onClick={() => handleEstado(a.id, 'aprobada')}>Aprobar</button>
                            <button className="btn btn-sm" style={{ background: '#ef4444', color: 'white', border: 'none' }} onClick={() => handleEstado(a.id, 'rechazada')}>Rechazar</button>
                          </>)}
                          {(a.usuario_id === user?.id || esPrincipal) && a.estado === 'pendiente' && (
                            <button className="btn btn-sm btn-outline" onClick={() => openAusModal(a)}>Editar</button>
                          )}
                          {(a.usuario_id === user?.id || esPrincipal) && (
                            <button className="btn btn-sm btn-danger" onClick={() => handleEliminarAus(a.id)}>Eliminar</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* Tab: Eventos */}
      {activeTab === 'eventos' && (
        eventos.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
            No hay eventos este mes. <button className="btn btn-sm btn-outline" style={{ marginLeft: 10 }} onClick={() => openEvModal()}>Crear evento</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {eventos.map(ev => (
              <div key={ev.id} className="card" style={{ padding: '14px 18px', borderLeft: `4px solid ${TIPOS_EVENTO[ev.tipo]?.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ background: TIPOS_EVENTO[ev.tipo]?.color, color: 'white', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{TIPOS_EVENTO[ev.tipo]?.label}</span>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{ev.titulo}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <span>📅 {ev.fecha_inicio?.slice(0, 10)}{ev.fecha_fin && ev.fecha_fin !== ev.fecha_inicio ? ` → ${ev.fecha_fin?.slice(0, 10)}` : ''}</span>
                      {!ev.todo_el_dia && ev.hora_inicio && <span>🕐 {ev.hora_inicio}{ev.hora_fin ? ` - ${ev.hora_fin}` : ''}</span>}
                      {ev.ubicacion && <span>📍 {ev.ubicacion}</span>}
                      {ev.comunidad_nombre && <span>🏢 {ev.comunidad_nombre}</span>}
                    </div>
                    {ev.descripcion && <div style={{ fontSize: 13, marginTop: 6, color: 'var(--text-secondary)' }}>{ev.descripcion}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => openEvModal(ev)}>Editar</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleEliminarEv(ev.id)}>Eliminar</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Tab: Vacaciones */}
      {activeTab === 'vacaciones' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Conteo de vacaciones laborables (lun-vie). Año: <strong>{anio}</strong></span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm btn-outline" onClick={() => setAnio(a => a - 1)}>◀ {anio - 1}</button>
              <button className="btn btn-sm btn-outline" onClick={() => setAnio(a => a + 1)}>{anio + 1} ▶</button>
            </div>
          </div>
          {vacaciones.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
              No hay empleados ni conserjes registrados
            </div>
          ) : (
            <div className="card">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nombre</th><th>Rol</th>
                      <th style={{ textAlign: 'center' }}>Días totales</th>
                      <th style={{ textAlign: 'center' }}>Usados</th>
                      <th style={{ textAlign: 'center' }}>Pendientes</th>
                      <th style={{ textAlign: 'center' }}>Disponibles</th>
                      <th style={{ textAlign: 'center' }}>Barra</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vacaciones.map(v => {
                      const pct = v.dias_totales > 0 ? Math.round((v.dias_usados / v.dias_totales) * 100) : 0;
                      const pctPend = v.dias_totales > 0 ? Math.round((v.dias_pendientes / v.dias_totales) * 100) : 0;
                      return (
                        <tr key={v.usuario_id}>
                          <td style={{ fontWeight: 500 }}>{v.nombre} {v.apellidos}</td>
                          <td><span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, background: v.rol === 'empleado' ? '#6366f120' : '#f59e0b20', color: v.rol === 'empleado' ? '#6366f1' : '#f59e0b', fontWeight: 600, textTransform: 'capitalize' }}>{v.rol}</span></td>
                          <td style={{ textAlign: 'center' }}>
                            {editingVac === v.usuario_id ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
                                <input type="number" min="0" max="365" value={vacDias} onChange={e => setVacDias(Number(e.target.value))}
                                  style={{ width: 60, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)', textAlign: 'center', fontSize: 13 }} />
                                <button className="btn btn-sm" style={{ background: '#10b981', color: 'white', border: 'none', padding: '2px 8px' }} onClick={() => handleSaveVac(v.usuario_id)}>✓</button>
                                <button className="btn btn-sm btn-outline" style={{ padding: '2px 6px' }} onClick={() => setEditingVac(null)}>✕</button>
                              </div>
                            ) : (
                              <span style={{ cursor: esPrincipal ? 'pointer' : 'default', textDecoration: esPrincipal ? 'underline dotted' : 'none', fontWeight: 600 }}
                                onClick={() => { if (esPrincipal) { setEditingVac(v.usuario_id); setVacDias(v.dias_totales); } }}>
                                {v.dias_totales}
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', color: '#10b981', fontWeight: 600 }}>{v.dias_usados}</td>
                          <td style={{ textAlign: 'center', color: '#f59e0b', fontWeight: 600 }}>{v.dias_pendientes}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: v.dias_disponibles <= 5 ? '#ef4444' : 'var(--text-primary)' }}>{v.dias_disponibles}</td>
                          <td style={{ minWidth: 120 }}>
                            <div style={{ height: 10, borderRadius: 5, background: 'var(--border)', overflow: 'hidden', position: 'relative' }}>
                              <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(pct, 100)}%`, background: '#10b981', borderRadius: 5 }} />
                              <div style={{ position: 'absolute', left: `${Math.min(pct, 100)}%`, top: 0, height: '100%', width: `${Math.min(pctPend, 100 - pct)}%`, background: '#f59e0b' }} />
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2, textAlign: 'center' }}>{pct}% usados</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {esPrincipal && <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10 }}>Haz clic en el número de "Días totales" para modificarlo.</p>}
            </div>
          )}
        </div>
      )}

      {/* Modal Ausencia */}
      {showAusModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowAusModal(false)}>
          <div ref={ausModalCardRef} className="card" style={{ maxWidth: 480, width: '100%', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', margin: 0 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20, fontWeight: 600 }}>{editingAus ? 'Editar ausencia' : 'Nueva ausencia'}</h3>
            <form onSubmit={handleAusSubmit}>
              {!editingAus && comunidades.length > 1 && (
                <div className="form-group">
                  <label className="form-label">Comunidad *</label>
                  <select className="form-control" value={ausForm.comunidad_id} onChange={e => setAusForm({ ...ausForm, comunidad_id: e.target.value })} required>
                    <option value="">Selecciona...</option>
                    {comunidades.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select className="form-control" value={ausForm.tipo} onChange={e => setAusForm({ ...ausForm, tipo: e.target.value })} required>
                  {Object.entries(TIPOS_AUSENCIA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Fecha inicio *</label>
                  <input type="date" className="form-control" value={ausForm.fecha_inicio} onChange={e => setAusForm({ ...ausForm, fecha_inicio: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha fin *</label>
                  <input type="date" className="form-control" value={ausForm.fecha_fin} onChange={e => setAusForm({ ...ausForm, fecha_fin: e.target.value })} required min={ausForm.fecha_inicio} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Motivo</label>
                <textarea className="form-control" value={ausForm.motivo} onChange={e => setAusForm({ ...ausForm, motivo: e.target.value })} rows={2} placeholder="Opcional..." />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowAusModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editingAus ? 'Guardar' : 'Solicitar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Evento */}
      {showEvModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowEvModal(false)}>
          <div ref={evModalCardRef} className="card" style={{ maxWidth: 480, width: '100%', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', margin: 0 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20, fontWeight: 600 }}>{editingEv ? 'Editar evento' : 'Nuevo evento'}</h3>
            <form onSubmit={handleEvSubmit}>
              <div className="form-group">
                <label className="form-label">Título *</label>
                <input type="text" className="form-control" value={evForm.titulo} onChange={e => setEvForm({ ...evForm, titulo: e.target.value })} required placeholder="Ej. Junta ordinaria enero" />
              </div>
              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select className="form-control" value={evForm.tipo} onChange={e => setEvForm({ ...evForm, tipo: e.target.value })} required>
                  {Object.entries(TIPOS_EVENTO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Fecha inicio *</label>
                  <input type="date" className="form-control" value={evForm.fecha_inicio} onChange={e => setEvForm({ ...evForm, fecha_inicio: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha fin</label>
                  <input type="date" className="form-control" value={evForm.fecha_fin} onChange={e => setEvForm({ ...evForm, fecha_fin: e.target.value })} min={evForm.fecha_inicio} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={evForm.todo_el_dia} onChange={e => setEvForm({ ...evForm, todo_el_dia: e.target.checked })} />
                  Todo el día
                </label>
              </div>
              {!evForm.todo_el_dia && (
                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">Hora inicio</label>
                    <input type="time" className="form-control" value={evForm.hora_inicio} onChange={e => setEvForm({ ...evForm, hora_inicio: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Hora fin</label>
                    <input type="time" className="form-control" value={evForm.hora_fin} onChange={e => setEvForm({ ...evForm, hora_fin: e.target.value })} />
                  </div>
                </div>
              )}
              {comunidades.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Comunidad asociada</label>
                  <select className="form-control" value={evForm.comunidad_id} onChange={e => setEvForm({ ...evForm, comunidad_id: e.target.value })}>
                    <option value="">Ninguna (personal)</option>
                    {comunidades.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowEvModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editingEv ? 'Guardar' : 'Crear evento'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
